import { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'

import { Icon } from '@src/elements/icon/icon'
import { ListCell } from '@src/components/table-list/cells'
import { Body2 } from '@src/elements/typography/typography'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { convertToLocalDate } from '@src/util/date-format'

import './flags-cell.sass'

@Component({
  computed: {
    selectedFlags () {
      return this.$store.state.members && this.$store.state.members.filters && this.$store.state.members.filters.flags ? this.$store.state.members.filters.flags : new Set<string>([])
    },
    expandedMemberIndex () {
      return this.$store.state.expandedMemberIndex
    }
  }
})
export class FlagsCell extends ListCell {

  @Prop({ required: false, default: false })
  showInBreadcrumb: boolean

  flagsExpanded: boolean = false

  expandedMemberIndex: number

  private flags: string[] = []

  constructor () {
    super()
  }

  beforeMount () {
    this.setRetentionFlags()
    this.$watch('item', () => {
      this.setRetentionFlags()
    })
  }

  mounted () {
    window.addEventListener('orientationchange', this.updateTabletFlagsExpandState)
  }

  beforeDestroy () {
    window.removeEventListener('orientationchange', this.updateTabletFlagsExpandState)
  }

  destroyed () {
    if (this.flagsExpanded) this.$store.dispatch('updateExpandedMemberIndex', -1)
  }

  updateTabletFlagsExpandState () {
    if (window.innerWidth > 1024) {
      this.$store.dispatch('updateExpandedMemberIndex', -1)
    }
  }

  /*
  1. No Alerts
  2. Terms & Conditions 					          - termsReacceptanceRequired
  3. RPE Alert 							                - easyWorkoutAlert - averageIntensityRating		      | The workouts of this member are too easy. The member RPE value is <averageIntensityRating> in the past 3 workouts
  4. Attendance Alert                       - attendanceAlert - lastWorkoutDate				          | This member was not training in the past 14/30/60 days. Last training date was <last_workout_date>
  5. Exercise Change Alert 				          - exercises_to_change_down, exercises_to_change_up	| Some of the exercises for this member need to be changed in order to keep the motivation high
  6. Workout Engagement Alerts  		        - stale_workout_alert									              | This member performed the same workout in the past 2 weeks. It is time to change his active workout to increase the engagement with your club.
  7. Active Workout Exercise change Alerts  - current_workout_alert 								            | Some of this users's active workout exercises need to be changed.
  8. Checkups Alert                         - plannedCheckupAlert - plannedCheckupDate          | There is a checkup planned for this week.
  */
  setRetentionFlags () {
    let flags: string[] = []

    // handle terms and conditions
    if (this.item['termsReacceptanceRequired']) {
      flags.push('termsReacceptanceRequired')
    }

    // handle progression
    const progression: any = this.item && typeof this.item.progression === 'object' ? this.item.progression : {}
    for (let flag in progression) {
      switch (flag) {
        case 'easyWorkoutAlert':
          if (progression.easyWorkoutAlert && progression.averageIntensityRating) {
            flags.push(flag)
          }
          break
        case 'attendanceAlert14':
          if (progression[flag] && progression.lastWorkoutDate && !progression['attendanceAlert30']) {
            flags.push(flag)
          }
          break
        case 'attendanceAlert30':
          if (progression[flag] && progression.lastWorkoutDate && !progression['attendanceAlert60']) {
            flags.push(flag)
          }
          break
        case 'attendanceAlert60':
          if (progression[flag] && progression.lastWorkoutDate) {
            flags.push(flag)
          }
          break
        case 'staleWorkoutAlert':
        case 'currentWorkoutAlert':
        case 'plannedCheckupAlert':
        case 'exercisesToChangeUp':
        case 'exercisesToChangeDown':
        case 'staleCheckupAlert':
          if (progression[flag]) {
            flags.push(flag)
          }
          break
        default:
        // do nothing
      }
    }
    this.flags = flags
  }

  redirectToDetails () {
    this.$router.push('/trainers/platform/members/' + this.item.id + '/status')
  }

  expandFlags () {
    if (this.flagsExpanded) {
      this.$store.dispatch('updateExpandedMemberIndex', -1)
    } else {
      this.$store.dispatch('updateExpandedMemberIndex', this.itemIndex)
    }
  }

  @Watch('expandedMemberIndex')
  updatedExpandState () {
    this.flagsExpanded = this.itemIndex === this.expandedMemberIndex
  }

  render (h: CreateElement) {
    let flagListLabel: any[] = []

    let landscapeFlags: any[] = []
    let tabletFlags: any[] = []
    this.flags.forEach((flag) => {
      const translationKey = flag === 'exercisesToChangeUp' || flag === 'exercisesToChangeDown' ? 'EXERCISESTOCHANGE' : flag.toUpperCase()
      flagListLabel.push(this.$t('PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.FILTERS.' + translationKey))

      landscapeFlags.push(<Tooltip content={this.composeTooltip(flag)}>
        <Icon name={`flags/${flag}`} class='flag-icon' />
      </Tooltip>)

      tabletFlags.push(<div class='tablet-flag-wrapper'>
        <div class='circle-wrapper'>
          <Icon name={`flags/${flag}`} class='flag-icon' />
        </div>
        <Body2>{this.$t('PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.FILTERS.' + translationKey)}</Body2>
      </div>)
    })

    return <div class='cell flags-wrapper'>
      <div class='flag-icons'>
        {landscapeFlags}
      </div>

      <div class='tablet-icons'>
        {tabletFlags}
      </div>

      {tabletFlags.length > 0 ? (
        <div class='tablet-alert-flags-icons'>
          <Icon class='alert-flags' name='util/circle-alert' />
          <Icon class='expand-alert-flags' click={this.expandFlags} name={this.flagsExpanded ? 'util/caret-up' : 'util/caret-down'} />
        </div>
      ) : ''}

      {
        !this.showInBreadcrumb ?
          <Body2 class='flag-list-label'>{flagListLabel.length > 0 ? flagListLabel.join(', ') : this.$t('PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.NO_ALERTS')}</Body2> : null
      }
    </div>
  }

  protected composeTooltip (flag: string) {
    switch (flag) {
      case 'easyWorkoutAlert':
        return this.$t('PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.ALERTS.EASYWORKOUTALERT', { valueForAlert: this.item.progression.averageIntensityRating })
      case 'attendanceAlert14':
      case 'attendanceAlert30':
      case 'attendanceAlert60':
        const date = convertToLocalDate(this.item.progression.lastWorkoutDate)
        return this.$t(`PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.ALERTS.${flag.toUpperCase()}`, { lastTrainingDate: date })
      case 'staleWorkoutAlert':
      case 'currentWorkoutAlert':
      case 'plannedCheckupAlert':
      case 'staleCheckupAlert':
        return this.$t(`PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.ALERTS.${flag.toUpperCase()}`)
      case 'exercisesToChangeUp':
      case 'exercisesToChangeDown':
        return this.$t('PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.ALERTS.EXERCISESTOCHANGE')
      case 'termsReacceptanceRequired':
        return this.$t('PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.ALERTS.TERMSREACCEPTANCEREQUIRED')
      default:
        return ''
    }
  }
}
